import { MOS_PACKAGE } from '@mxa-argos/constants/mos';
import {
  LOCATION_PERMISSIONS,
  NOTIFICATION_LISTENER,
} from '@mxa-argos/modules/forja/constants/forja.constants';
import type { ForjaStep } from '@mxa-argos/modules/forja/interfaces/forja-step.interface';
import type { ForjaDeviceSession } from '@mxa-argos/modules/forja/lib/engine/forja-device-session';
import { duraspeedExclusionStep } from '@mxa-argos/modules/forja/lib/provisioning/steps/duraspeed.step';

export function phaseBSteps(): ForjaStep[] {
  return [
    {
      name: 'location enabled (high accuracy)',
      verify: async (device) =>
        (await device.shell('settings', 'get', 'secure', 'location_mode')) === '3',
      apply: async (device) => {
        await device.shell('settings', 'put', 'secure', 'location_mode', '3');
      },
      verifyTimeoutMs: 5_000,
    },
    ...LOCATION_PERMISSIONS.map(locationPermissionStep),
    {
      name: 'default dialer = MOS',
      verify: verifyDialer,
      apply: async (device) => {
        await device.shell(
          'cmd',
          'role',
          'add-role-holder',
          'android.app.role.DIALER',
          MOS_PACKAGE,
        );
      },
      verifyTimeoutMs: 5_000,
    },
    {
      name: 'notification access = MOS',
      verify: async (device) =>
        (
          await device.shell('settings', 'get', 'secure', 'enabled_notification_listeners')
        ).includes(`${MOS_PACKAGE}/`),
      apply: async (device) => {
        await device.shell('cmd', 'notification', 'allow_listener', NOTIFICATION_LISTENER);
      },
      verifyTimeoutMs: 5_000,
    },
    {
      name: 'deviceidle whitelist MOS',
      verify: async (device) =>
        (await device.shell('dumpsys', 'deviceidle', 'whitelist')).includes(MOS_PACKAGE),
      apply: async (device) => {
        await device.shell('dumpsys', 'deviceidle', 'whitelist', `+${MOS_PACKAGE}`);
      },
      verifyTimeoutMs: 5_000,
    },
    duraspeedExclusionStep(),
  ];
}

function locationPermissionStep(permission: string): ForjaStep {
  const shortName = permission.split('.').pop();

  return {
    name: `MOS permission ${shortName}`,
    verify: async (device) =>
      (await device.shell('dumpsys', 'package', MOS_PACKAGE)).includes(
        `${permission}: granted=true`,
      ),
    apply: async (device) => {
      await device.shell('pm', 'grant', MOS_PACKAGE, permission);
    },
    verifyTimeoutMs: 5_000,
  };
}

async function verifyDialer(device: ForjaDeviceSession): Promise<boolean> {
  const holders = await device.shell('cmd', 'role', 'get-role-holders', 'android.app.role.DIALER');

  if (holders.includes(MOS_PACKAGE) && !holders.includes('Usage')) {
    return true;
  }

  const dump = await device.shell('dumpsys', 'role');
  const index = dump.indexOf('android.app.role.DIALER');

  if (index === -1) {
    return false;
  }

  return dump.slice(index, index + 300).includes(MOS_PACKAGE);
}
